import { useEffect, useMemo, useRef, useState } from "react";
import { AppIcon, MetricCard } from "../components/InstateamMasterComponents";
import {
  downloadAdminPayslip,
  getAdminPayrollOverview,
  importAdminPayroll,
  processAdminPayroll,
  updateAdminPayrollRecord,
  updateAdminPayrollSettings,
} from "../api/instateamPayrollAdminApi";
import {
  downloadPayrollCsv,
  formatMonthHeading,
  getCurrentMonthKey,
  getPayrollStatusMeta,
  parsePayrollCsv,
  PAYROLL_PAGE_SIZE_OPTIONS,
  PAYROLL_STATUS_OPTIONS,
  TAX_REGIME_OPTIONS,
  triggerBlobDownload,
} from "../utils/adminPayrollHelpers";
import { currencyFormatter, getInitials, toAbsoluteUrl } from "../utils/adminHelpers";

const TABS = [
  { key: "runs", label: "Payroll Runs", status: "all" },
  { key: "employees", label: "All Employees", status: "all" },
  { key: "processed", label: "Processed", status: "processed" },
  { key: "pending", label: "Pending", status: "pending" },
  { key: "failed", label: "Failed", status: "failed" },
];

const INITIAL_SETTINGS_FORM = {
  defaultHraPercent: "40",
  defaultConveyanceAllowance: "3200",
  defaultPfPercent: "12",
  defaultProfessionalTax: "200",
  defaultTaxRegime: "Old Regime",
  payrollPaymentDay: "31",
};

const INITIAL_PAYROLL_FORM = {
  monthKey: getCurrentMonthKey(),
  status: "pending",
  basicSalary: "",
  houseRentAllowance: "",
  conveyanceAllowance: "",
  specialAllowance: "",
  providentFund: "",
  professionalTax: "",
  incomeTax: "",
  payFrequency: "Monthly",
  taxRegime: "Old Regime",
  panNumber: "",
  uanNumber: "",
  pfNumber: "",
  paymentDateKey: "",
};

const EMPTY_SUMMARY = {
  processed: 0,
  pending: 0,
  failed: 0,
  notProcessed: 0,
};

function PayrollSettingsModal({
  form,
  onChange,
  onClose,
  onSubmit,
  isSubmitting,
}) {
  return (
    <div className="modal-overlay" role="dialog" aria-modal="true">
      <div className="department-modal payroll-settings-modal">
        <div className="employee-modal-header">
          <div className="employee-modal-title">
            <h3>Payroll Settings</h3>
            <p>Update the default payroll assumptions used across salary calculations.</p>
          </div>

          <button className="icon-button" type="button" onClick={onClose}>
            <AppIcon name="close" />
          </button>
        </div>

        <form className="department-modal-form" onSubmit={onSubmit}>
          <div className="attendance-form-grid payroll-settings-grid">
            {[
              ["Default HRA (%)", "defaultHraPercent"],
              ["Default Conveyance (₹)", "defaultConveyanceAllowance"],
              ["Default PF (%)", "defaultPfPercent"],
              ["Default Professional Tax (₹)", "defaultProfessionalTax"],
              ["Payroll Payment Day", "payrollPaymentDay"],
            ].map(([label, key]) => (
              <label key={key} className="form-field">
                <span>{label}</span>
                <div className="field-shell">
                  <input
                    type="number"
                    value={form[key]}
                    onChange={(event) =>
                      onChange((current) => ({
                        ...current,
                        [key]: event.target.value,
                      }))
                    }
                  />
                </div>
              </label>
            ))}

            <label className="form-field">
              <span>Default Tax Regime</span>
              <div className="field-shell select-shell">
                <select
                  value={form.defaultTaxRegime}
                  onChange={(event) =>
                    onChange((current) => ({
                      ...current,
                      defaultTaxRegime: event.target.value,
                    }))
                  }
                >
                  {TAX_REGIME_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                <AppIcon name="chevronDown" />
              </div>
            </label>
          </div>

          <div className="form-footer">
            <span className="required-note">These values are used when salary components are not filled manually.</span>
            <div className="form-footer-actions">
              <button className="secondary-button" type="button" onClick={onClose}>
                Cancel
              </button>
              <button className="primary-button save-button" type="submit">
                {isSubmitting ? "Saving..." : "Save Settings"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

function SalaryComponentsModal({ settings, onClose }) {
  if (!settings) return null;

  const componentRows = [
    {
      label: "Basic Salary",
      copy: "The fixed core monthly pay for the employee.",
      tone: "success",
    },
    {
      label: `HRA (${settings.defaultHraPercent}%)`,
      copy: "Automatically suggested from basic salary unless manually overridden.",
      tone: "info",
    },
    {
      label: "Conveyance Allowance",
      copy: `Default monthly transport allowance is ${currencyFormatter.format(
        Number(settings.defaultConveyanceAllowance || 0)
      )}.`,
      tone: "warning",
    },
    {
      label: `Provident Fund (${settings.defaultPfPercent}%)`,
      copy: "Suggested PF deduction from basic salary for payroll processing.",
      tone: "violet",
    },
  ];

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true">
      <div className="department-modal payroll-components-modal">
        <div className="employee-modal-header">
          <div className="employee-modal-title">
            <h3>Salary Components</h3>
            <p>Reference how the payroll breakdown is structured for each employee.</p>
          </div>

          <button className="icon-button" type="button" onClick={onClose}>
            <AppIcon name="close" />
          </button>
        </div>

        <div className="payroll-components-grid">
          {componentRows.map((component) => (
            <article key={component.label} className="payroll-component-card">
              <div className={`attendance-quick-icon ${component.tone}`}>
                <AppIcon name="payroll" />
              </div>
              <div>
                <strong>{component.label}</strong>
                <p>{component.copy}</p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
}

function PayrollEditModal({
  row,
  form,
  onChange,
  onClose,
  onSubmit,
  isSubmitting,
}) {
  if (!row) return null;

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true">
      <div className="department-modal payroll-edit-modal">
        <div className="employee-modal-header">
          <div className="employee-modal-title">
            <h3>Edit Payroll</h3>
            <p>Adjust salary components and payroll status for {row.fullName}.</p>
          </div>

          <button className="icon-button" type="button" onClick={onClose}>
            <AppIcon name="close" />
          </button>
        </div>

        <form className="department-modal-form" onSubmit={onSubmit}>
          <div className="attendance-form-grid payroll-edit-grid">
            {[
              ["Basic Salary (₹)", "basicSalary"],
              ["House Rent Allowance (₹)", "houseRentAllowance"],
              ["Conveyance Allowance (₹)", "conveyanceAllowance"],
              ["Special Allowance (₹)", "specialAllowance"],
              ["Provident Fund (₹)", "providentFund"],
              ["Professional Tax (₹)", "professionalTax"],
              ["Income Tax / TDS (₹)", "incomeTax"],
            ].map(([label, key]) => (
              <label key={key} className="form-field">
                <span>{label}</span>
                <div className="field-shell">
                  <input
                    type="number"
                    value={form[key]}
                    onChange={(event) =>
                      onChange((current) => ({
                        ...current,
                        [key]: event.target.value,
                      }))
                    }
                  />
                </div>
              </label>
            ))}

            <label className="form-field">
              <span>Payroll Status</span>
              <div className="field-shell select-shell">
                <select
                  value={form.status}
                  onChange={(event) =>
                    onChange((current) => ({
                      ...current,
                      status: event.target.value,
                    }))
                  }
                >
                  <option value="processed">Processed</option>
                  <option value="pending">Pending</option>
                  <option value="failed">Failed</option>
                </select>
                <AppIcon name="chevronDown" />
              </div>
            </label>

            <label className="form-field">
              <span>Payment Date</span>
              <div className="field-shell">
                <input
                  type="date"
                  value={form.paymentDateKey}
                  onChange={(event) =>
                    onChange((current) => ({
                      ...current,
                      paymentDateKey: event.target.value,
                    }))
                  }
                  disabled={form.status !== "processed"}
                />
                <AppIcon name="calendar" />
              </div>
            </label>

            <label className="form-field">
              <span>Pay Frequency</span>
              <div className="field-shell select-shell">
                <select
                  value={form.payFrequency}
                  onChange={(event) =>
                    onChange((current) => ({
                      ...current,
                      payFrequency: event.target.value,
                    }))
                  }
                >
                  <option value="Monthly">Monthly</option>
                  <option value="Bi-Weekly">Bi-Weekly</option>
                  <option value="Weekly">Weekly</option>
                </select>
                <AppIcon name="chevronDown" />
              </div>
            </label>

            <label className="form-field">
              <span>Tax Regime</span>
              <div className="field-shell select-shell">
                <select
                  value={form.taxRegime}
                  onChange={(event) =>
                    onChange((current) => ({
                      ...current,
                      taxRegime: event.target.value,
                    }))
                  }
                >
                  {TAX_REGIME_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                <AppIcon name="chevronDown" />
              </div>
            </label>

            <label className="form-field">
              <span>PAN Number</span>
              <div className="field-shell">
                <input
                  type="text"
                  value={form.panNumber}
                  onChange={(event) =>
                    onChange((current) => ({
                      ...current,
                      panNumber: event.target.value,
                    }))
                  }
                />
              </div>
            </label>

            <label className="form-field">
              <span>UAN Number</span>
              <div className="field-shell">
                <input
                  type="text"
                  value={form.uanNumber}
                  onChange={(event) =>
                    onChange((current) => ({
                      ...current,
                      uanNumber: event.target.value,
                    }))
                  }
                />
              </div>
            </label>

            <label className="form-field">
              <span>PF Number</span>
              <div className="field-shell">
                <input
                  type="text"
                  value={form.pfNumber}
                  onChange={(event) =>
                    onChange((current) => ({
                      ...current,
                      pfNumber: event.target.value,
                    }))
                  }
                />
              </div>
            </label>
          </div>

          <div className="form-footer">
            <span className="required-note">Saving here updates both the monthly payroll record and the employee’s master salary profile.</span>
            <div className="form-footer-actions">
              <button className="secondary-button" type="button" onClick={onClose}>
                Cancel
              </button>
              <button className="primary-button save-button" type="submit">
                {isSubmitting ? "Saving..." : "Save Payroll"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

function EmployeeAvatar({ row }) {
  if (row?.profilePhoto?.url) {
    return (
      <img
        src={toAbsoluteUrl(row.profilePhoto.url)}
        alt={row.fullName}
        className="attendance-person-avatar"
      />
    );
  }

  return <div className="avatar-circle small">{getInitials(row?.fullName)}</div>;
}

export default function PayrollPage({
  token,
  departmentOptions,
  searchText,
  onProtectedError,
  onNotify,
}) {
  const [monthKey, setMonthKey] = useState(getCurrentMonthKey);
  const [activeTab, setActiveTab] = useState("runs");
  const [departmentFilter, setDepartmentFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [overview, setOverview] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showComponentsModal, setShowComponentsModal] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [settingsForm, setSettingsForm] = useState(INITIAL_SETTINGS_FORM);
  const [payrollForm, setPayrollForm] = useState(INITIAL_PAYROLL_FORM);
  const [openActionMenuId, setOpenActionMenuId] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, departmentFilter, statusFilter, pageSize]);

  useEffect(() => {
    if (statusFilter === "all") {
      if (activeTab !== "runs" && activeTab !== "employees") {
        setActiveTab("runs");
      }
      return;
    }

    if (activeTab !== statusFilter) {
      setActiveTab(statusFilter);
    }
  }, [activeTab, statusFilter]);

  useEffect(() => {
    let isActive = true;

    async function fetchOverview() {
      try {
        setIsLoading(true);
        const payload = await getAdminPayrollOverview(token, {
          month: monthKey,
          department: departmentFilter,
          status: statusFilter,
          search: searchText,
        });

        if (!isActive) return;

        setOverview(payload.overview || null);
        setSettingsForm((current) => ({
          ...current,
          ...Object.fromEntries(
            Object.entries(payload.overview?.settings || {}).map(([key, value]) => [
              key,
              String(value ?? ""),
            ])
          ),
        }));
      } catch (error) {
        if (!isActive) return;
        onNotify?.({
          type: "error",
          text: error.message,
        });
        onProtectedError?.(error);
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    }

    fetchOverview();

    return () => {
      isActive = false;
    };
  }, [departmentFilter, monthKey, onNotify, onProtectedError, searchText, statusFilter, token]);

  const metrics = overview?.metrics || {
    totalEmployees: 0,
    processedThisMonth: 0,
    pendingThisMonth: 0,
    failedThisMonth: 0,
    totalPayrollValue: 0,
  };

  const summary = useMemo(() => overview?.summary || EMPTY_SUMMARY, [overview?.summary]);

  const rows = overview?.tableRows || [];
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedRows = rows.slice((safePage - 1) * pageSize, safePage * pageSize);
  const pendingCount = Number(summary.pending || 0) + Number(summary.notProcessed || 0);

  function handleTabChange(tab) {
    setActiveTab(tab.key);
    setStatusFilter(tab.status);
  }

  async function refreshOverview(nextOverrides = {}) {
    const payload = await getAdminPayrollOverview(token, {
      month: nextOverrides.monthKey || monthKey,
      department: nextOverrides.departmentFilter || departmentFilter,
      status: nextOverrides.statusFilter || statusFilter,
      search: nextOverrides.searchText ?? searchText,
    });
    setOverview(payload.overview || null);
  }

  async function handleApplyFilters() {
    try {
      setIsLoading(true);
      await refreshOverview();
    } catch (error) {
      onNotify?.({
        type: "error",
        text: error.message,
      });
      onProtectedError?.(error);
    } finally {
      setIsLoading(false);
    }
  }

  function openEditModal(row) {
    setEditingRow(row);
    setOpenActionMenuId(null);
    setPayrollForm({
      monthKey: row.monthKey || monthKey,
      status: row.status === "not_processed" ? "pending" : row.status,
      basicSalary: String(row.earnings?.basicSalary ?? ""),
      houseRentAllowance: String(row.earnings?.houseRentAllowance ?? ""),
      conveyanceAllowance: String(row.earnings?.conveyanceAllowance ?? ""),
      specialAllowance: String(row.earnings?.specialAllowance ?? ""),
      providentFund: String(row.deductions?.providentFund ?? ""),
      professionalTax: String(row.deductions?.professionalTax ?? ""),
      incomeTax: String(row.deductions?.incomeTax ?? ""),
      payFrequency: row.payFrequency || "Monthly",
      taxRegime: row.taxRegime || "Old Regime",
      panNumber: row.panNumber || "",
      uanNumber: row.uanNumber || "",
      pfNumber: row.pfNumber || "",
      paymentDateKey: row.paymentDate ? row.paymentDate.slice(0, 10) : "",
    });
  }

  async function handleSubmitPayroll(event) {
    event.preventDefault();
    if (!editingRow) return;

    try {
      setIsSubmitting(true);
      await updateAdminPayrollRecord(token, editingRow.employeeObjectId, payrollForm);
      setEditingRow(null);
      await refreshOverview();
      onNotify?.({
        type: "success",
        text: "Payroll details updated successfully.",
      });
    } catch (error) {
      onNotify?.({
        type: "error",
        text: error.message,
      });
      onProtectedError?.(error);
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleProcessPayroll() {
    const confirmMessage = `Process payroll for ${formatMonthHeading(monthKey)}${
      departmentFilter !== "all" ? ` in ${departmentFilter}` : ""
    }?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setIsSubmitting(true);
      const payload = await processAdminPayroll(token, {
        monthKey,
        department: departmentFilter,
      });
      await refreshOverview();
      onNotify?.({
        type: "success",
        text: payload.message || "Payroll processed successfully.",
      });
    } catch (error) {
      onNotify?.({
        type: "error",
        text: error.message,
      });
      onProtectedError?.(error);
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleSubmitSettings(event) {
    event.preventDefault();

    try {
      setIsSubmitting(true);
      const payload = await updateAdminPayrollSettings(token, settingsForm);
      setShowSettingsModal(false);
      setSettingsForm((current) => ({
        ...current,
        ...Object.fromEntries(
          Object.entries(payload.settings || {}).map(([key, value]) => [key, String(value ?? "")])
        ),
      }));
      await refreshOverview();
      onNotify?.({
        type: "success",
        text: payload.message || "Payroll settings updated successfully.",
      });
    } catch (error) {
      onNotify?.({
        type: "error",
        text: error.message,
      });
      onProtectedError?.(error);
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleImportClick() {
    fileInputRef.current?.click();
  }

  async function handleImportFile(event) {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsSubmitting(true);
      const fileText = await file.text();
      const rowsToImport = parsePayrollCsv(fileText);

      if (!rowsToImport.length) {
        throw new Error("The selected payroll CSV does not contain any valid rows.");
      }

      const payload = await importAdminPayroll(token, rowsToImport);
      await refreshOverview();
      onNotify?.({
        type: "success",
        text: payload.message || "Payroll rows imported successfully.",
      });
    } catch (error) {
      onNotify?.({
        type: "error",
        text: error.message,
      });
      onProtectedError?.(error);
    } finally {
      event.target.value = "";
      setIsSubmitting(false);
    }
  }

  function handleExportPayroll() {
    const exportRows = [
      [
        "Employee",
        "Employee ID",
        "Department",
        "Month",
        "CTC Annual",
        "Net Salary",
        "Status",
        "Payment Date",
      ],
      ...rows.map((row) => [
        row.fullName,
        row.employeeId,
        row.department,
        row.monthKey || row.monthLabel,
        row.totals?.ctcAnnual || 0,
        row.totals?.netSalary || 0,
        row.statusLabel || row.status,
        row.paymentDate ? String(row.paymentDate).slice(0, 10) : "-",
      ]),
    ];

    downloadPayrollCsv(`instateam-payroll-${monthKey}.csv`, exportRows);
  }

  async function handleDownloadPayslip(row) {
    if (!row?._id || row.isPreview || row.status === "not_processed") {
      onNotify?.({
        type: "warning",
        text: "Process this employee payroll first to download a payslip.",
      });
      return;
    }

    try {
      setIsSubmitting(true);
      const payload = await downloadAdminPayslip(token, row._id);
      triggerBlobDownload(payload.blob, payload.fileName);
    } catch (error) {
      onNotify?.({
        type: "error",
        text: error.message,
      });
      onProtectedError?.(error);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <section className="admin-payroll-page">
      <div className="attendance-page-actions payroll-page-actions">
        <button
          className="secondary-button employee-inline-button"
          type="button"
          onClick={() => setShowSettingsModal(true)}
        >
          <AppIcon name="settings" />
          Payroll Settings
        </button>
        <button
          className="secondary-button employee-inline-button"
          type="button"
          onClick={() => setShowComponentsModal(true)}
        >
          <AppIcon name="documents" />
          Salary Components
        </button>
        <button
          className="secondary-button employee-inline-button"
          type="button"
          onClick={handleImportClick}
        >
          <AppIcon name="export" />
          Import Payroll
        </button>
        <button
          className="primary-button employee-inline-button"
          type="button"
          onClick={handleProcessPayroll}
          disabled={isSubmitting}
        >
          <AppIcon name="plus" />
          Process Payroll
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,text/csv"
          hidden
          onChange={handleImportFile}
        />
      </div>

      <div className="metrics-grid admin-payroll-metrics-grid">
        <MetricCard
          label="Total Employees"
          value={metrics.totalEmployees}
          helper="Active employees"
          icon="employees"
          tone="success"
        />
        <MetricCard
          label="Processed This Month"
          value={metrics.processedThisMonth}
          helper="Employees"
          icon="calendar"
        />
        <MetricCard
          label="Pending"
          value={metrics.pendingThisMonth}
          helper="Employees"
          icon="clock"
          tone="warning"
        />
        <MetricCard
          label="Failed"
          value={metrics.failedThisMonth}
          helper="Employees"
          icon="complaints"
          tone="danger"
        />
        <MetricCard
          label={`Total Payroll (${formatMonthHeading(monthKey)})`}
          value={currencyFormatter.format(Number(metrics.totalPayrollValue || 0))}
          helper="Total disbursement"
          icon="payroll"
          tone="violet"
        />
      </div>

      <div className="admin-payroll-layout">
        <div className="table-card admin-payroll-main-card">
          <div className="admin-payroll-tabs">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={`admin-payroll-tab ${activeTab === tab.key ? "active" : ""}`}
                onClick={() => handleTabChange(tab)}
              >
                {tab.key === "processed"
                  ? `${tab.label} (${summary.processed})`
                  : tab.key === "pending"
                    ? `${tab.label} (${pendingCount})`
                    : tab.key === "failed"
                      ? `${tab.label} (${summary.failed})`
                      : tab.label}
              </button>
            ))}
          </div>

          <div className="admin-payroll-filter-row">
            <label className="inline-search-field payroll-month-field">
              <input
                type="month"
                value={monthKey}
                onChange={(event) => setMonthKey(event.target.value || getCurrentMonthKey())}
              />
            </label>

            <label className="inline-select-field">
              <select
                value={departmentFilter}
                onChange={(event) => setDepartmentFilter(event.target.value)}
              >
                <option value="all">All Departments</option>
                {departmentOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <AppIcon name="chevronDown" />
            </label>

            <label className="inline-select-field">
              <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                {PAYROLL_STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <AppIcon name="chevronDown" />
            </label>

            <button
              className="secondary-button payroll-filter-button"
              type="button"
              onClick={handleApplyFilters}
            >
              <AppIcon name="filter" />
              Apply Filters
            </button>
          </div>

          <div className="table-shell">
            <table className="employee-table admin-payroll-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Employee ID</th>
                  <th>Department</th>
                  <th>CTC (Annual)</th>
                  <th>Net Salary</th>
                  <th>Status</th>
                  <th>Payment Date</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {!isLoading && !paginatedRows.length ? (
                  <tr>
                    <td className="empty-table-state" colSpan={8}>
                      No payroll entries found for the selected filters.
                    </td>
                  </tr>
                ) : null}

                {paginatedRows.map((row) => {
                  const statusMeta = getPayrollStatusMeta(row.status);

                  return (
                    <tr key={`${row.employeeId}-${row.monthKey}`}>
                      <td>
                        <div className="employee-cell">
                          <EmployeeAvatar row={row} />
                          <div>
                            <strong>{row.fullName}</strong>
                            <p>{row.designation || "-"}</p>
                          </div>
                        </div>
                      </td>
                      <td>{row.employeeId}</td>
                      <td>{row.department || "-"}</td>
                      <td>{currencyFormatter.format(Number(row.totals?.ctcAnnual || 0))}</td>
                      <td>{currencyFormatter.format(Number(row.totals?.netSalary || 0))}</td>
                      <td>
                        <span className={`status-badge ${statusMeta.tone}`}>
                          {statusMeta.label}
                        </span>
                      </td>
                      <td>{row.paymentDateLabel || "-"}</td>
                      <td className="department-actions-cell">
                        <button
                          className="secondary-icon-button"
                          type="button"
                          onClick={() =>
                            setOpenActionMenuId((current) =>
                              current === row.employeeId ? null : row.employeeId
                            )
                          }
                        >
                          <AppIcon name="more" />
                        </button>

                        {openActionMenuId === row.employeeId ? (
                          <div className="department-action-menu payroll-action-menu">
                            <button className="payroll-menu-action edit" type="button" onClick={() => openEditModal(row)}>
                              <AppIcon name="edit" />
                              Edit Payroll
                            </button>
                            <button className="payroll-menu-action download" type="button" onClick={() => handleDownloadPayslip(row)}>
                              <AppIcon name="download" />
                              Download Payslip
                            </button>
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="table-footer">
            <span>
              Showing {paginatedRows.length ? (safePage - 1) * pageSize + 1 : 0} to{" "}
              {Math.min(safePage * pageSize, rows.length)} of {rows.length} employees
            </span>

            <div className="table-footer-actions">
              <div className="pagination">
                <button
                  className="page-chip"
                  type="button"
                  onClick={() => setCurrentPage((current) => Math.max(1, current - 1))}
                  disabled={safePage === 1}
                >
                  <AppIcon name="chevronLeft" />
                </button>
                <button className="page-chip active" type="button">
                  {safePage}
                </button>
                <button
                  className="page-chip"
                  type="button"
                  onClick={() =>
                    setCurrentPage((current) => Math.min(totalPages, current + 1))
                  }
                  disabled={safePage === totalPages}
                >
                  <AppIcon name="chevronRight" />
                </button>
              </div>

              <label className="inline-select-field compact">
                <select
                  value={pageSize}
                  onChange={(event) => setPageSize(Number(event.target.value))}
                >
                  {PAYROLL_PAGE_SIZE_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option} / page
                    </option>
                  ))}
                </select>
                <AppIcon name="chevronDown" />
              </label>
            </div>
          </div>
        </div>

        <div className="admin-payroll-side">
          <div className="table-card admin-payroll-summary-card">
            <div className="attendance-side-header">
              <div>
                <h3>Payroll Summary ({formatMonthHeading(monthKey)})</h3>
              </div>
            </div>

            <div className="payroll-summary-clean">
              <div className="payroll-summary-total">
                <span>Total Employees</span>
                <strong>{metrics.totalEmployees}</strong>
              </div>

              <div className="payroll-summary-list">
                {[
                  ["Processed", summary.processed, "#67c97c"],
                  ["Pending", summary.pending, "#ffbf47"],
                  ["Failed", summary.failed, "#ff6f6f"],
                  ["Not Processed", summary.notProcessed, "#b8beca"],
                ].map(([label, value, color]) => (
                  <div key={label} className="payroll-summary-row">
                    <span><i style={{ background: color }} />{label}</span>
                    <small>{value}</small>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="table-card admin-payroll-history-card">
            <div className="attendance-side-header">
              <div>
                <h3>Payroll Run History</h3>
              </div>
            </div>

            <div className="admin-payroll-history-list">
              {(overview?.runHistory || []).length ? (
                overview.runHistory.map((entry) => (
                  <article key={entry.monthKey} className="admin-payroll-history-item">
                    <div>
                      <strong>{entry.monthLabel}</strong>
                      <p>{entry.paymentDateLabel || "Awaiting payment date"}</p>
                    </div>
                    <div className="admin-payroll-history-meta">
                      <span className="status-badge active">Completed</span>
                      <strong>{currencyFormatter.format(Number(entry.totalNetSalary || 0))}</strong>
                    </div>
                  </article>
                ))
              ) : (
                <p className="documents-empty">Payroll run history will appear once payroll is processed.</p>
              )}
            </div>
          </div>

          <div className="table-card admin-payroll-quick-card">
            <div className="attendance-side-header">
              <div>
                <h3>Quick Actions</h3>
              </div>
            </div>

            <div className="attendance-quick-actions-grid">
              <button className="attendance-quick-action" type="button" onClick={handleProcessPayroll}>
                <div className="attendance-quick-icon success">
                  <AppIcon name="payroll" />
                </div>
                <span>Process Payroll</span>
              </button>
              <button className="attendance-quick-action" type="button" onClick={handleImportClick}>
                <div className="attendance-quick-icon warning">
                  <AppIcon name="documents" />
                </div>
                <span>Bulk Update</span>
              </button>
              <button
                className="attendance-quick-action"
                type="button"
                onClick={() =>
                  onNotify?.({
                    type: "warning",
                    text: "Export the payroll report or download individual payslips from each employee row.",
                  })
                }
              >
                <div className="attendance-quick-icon violet">
                  <AppIcon name="mail" />
                </div>
                <span>Payslip Email</span>
              </button>
              <button className="attendance-quick-action" type="button" onClick={handleExportPayroll}>
                <div className="attendance-quick-icon info">
                  <AppIcon name="reports" />
                </div>
                <span>Payroll Report</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {showSettingsModal ? (
        <PayrollSettingsModal
          form={settingsForm}
          onChange={setSettingsForm}
          onClose={() => setShowSettingsModal(false)}
          onSubmit={handleSubmitSettings}
          isSubmitting={isSubmitting}
        />
      ) : null}

      {showComponentsModal ? (
        <SalaryComponentsModal
          settings={overview?.settings || null}
          onClose={() => setShowComponentsModal(false)}
        />
      ) : null}

      {editingRow ? (
        <PayrollEditModal
          row={editingRow}
          form={payrollForm}
          onChange={setPayrollForm}
          onClose={() => setEditingRow(null)}
          onSubmit={handleSubmitPayroll}
          isSubmitting={isSubmitting}
        />
      ) : null}
    </section>
  );
}
